(() => {
  if (customElements.get('zc-it-estimator')) return;
  class ITServiceEstimator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.querySelector('[data-calculator]').hidden = false;
      const counts = [...this.querySelectorAll('input[type="number"]')];
      counts.forEach(input => {
        input.dataset.lastValid = input.value;
        input.addEventListener('keydown', event => {
          if (['.', ',', '-', '+', 'e', 'E'].includes(event.key) && !event.ctrlKey && !event.metaKey) event.preventDefault();
        });
        input.addEventListener('blur', () => {
          if (input.value === '') {
            input.value = input.dataset.lastValid;
            this.querySelector('[data-input-feedback]').textContent = 'A number is required. Your previous value has been restored.';
            this.update();
          }
        });
      });
      this.addEventListener('input', event => {
        const input = event.target;
        if (counts.includes(input)) {
          const feedback = this.querySelector('[data-input-feedback]');
          feedback.textContent = '';
          if (input.value !== '') {
            const value = Number(input.value);
            if (!Number.isInteger(value) || value < Number(input.min) || value > Number(input.max)) {
              input.value = input.dataset.lastValid;
              feedback.textContent = value > Number(input.max)
                ? 'That value exceeds the calculator’s range and was not applied. Request a tailored estimate for a larger project.'
                : 'Enter a whole number within the listed range. Your previous value has been kept.';
            } else {
              input.dataset.lastValid = input.value;
            }
          }
        }
        this.update();
      });
      this.setupEmail();
      this.update();
    }
    setupEmail() {
      const dialog = this.querySelector('dialog');
      const form = this.querySelector('[data-email-form]');
      const submit = this.querySelector('[data-email-submit]');
      const error = this.querySelector('[data-email-error]');
      const entry = this.querySelector('[data-email-entry]');
      const success = this.querySelector('[data-email-success]');
      const close = () => { if (!this.sending) dialog.close(); };
      this.querySelector('[data-email-close]').addEventListener('click', close);
      this.querySelector('[data-email-done]').addEventListener('click', close);
      dialog.addEventListener('cancel', event => { if (this.sending) event.preventDefault(); });
      dialog.addEventListener('close', () => this.querySelector('[data-send]').focus());
      this.querySelector('[data-send]').addEventListener('click', async () => {
        if (!this.estimatePayload) return;
        this.emailEstimate = { ...this.estimatePayload };
        this.emailToken = '';
        submit.disabled = true;
        entry.hidden = false;
        success.hidden = true;
        error.textContent = '';
        dialog.setAttribute('aria-labelledby', entry.querySelector('h2').id);
        dialog.showModal();
        form.elements.email.focus();
        try {
          if (!window.turnstile) {
            if (!window.zcEstimatorTurnstileLoading) {
              window.zcEstimatorTurnstileLoading = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
                const failed = () => { script.remove(); window.zcEstimatorTurnstileLoading = null; reject(new Error('verification_unavailable')); };
                const loadTimeout = setTimeout(failed, 15000);
                script.onload = () => { clearTimeout(loadTimeout); resolve(); };
                script.onerror = () => { clearTimeout(loadTimeout); failed(); };
                document.head.append(script);
              });
            }
            await window.zcEstimatorTurnstileLoading;
          }
          if (this.widget !== undefined) window.turnstile.reset(this.widget);
          else this.widget = window.turnstile.render(this.querySelector('[data-email-verification]'), {
            sitekey: this.dataset.sitekey, theme: 'light',
            callback: token => { this.emailToken = token; submit.disabled = this.sending || false; if (error.textContent.startsWith('Verification')) error.textContent = ''; },
            'expired-callback': () => { this.emailToken = ''; submit.disabled = true; },
            'error-callback': () => { this.emailToken = ''; submit.disabled = true; error.textContent = 'Verification could not load. Close this window and try again.'; }
          });
        } catch {
          error.textContent = 'Verification could not load. Close this window and try again.';
        }
      });
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (this.sending || !this.emailToken || !form.reportValidity()) return;
        const email = form.elements.email.value.trim();
        this.sending = true;
        submit.disabled = true;
        submit.textContent = 'Sending…';
        this.querySelector('[data-email-close]').disabled = true;
        error.textContent = '';
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 45000);
        try {
          const response = await fetch(this.dataset.intakeUrl, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
            body: JSON.stringify({ formSource: 'it-service-estimate', contact: { email }, estimate: this.emailEstimate,
              hp: form.elements.website.value, turnstileToken: this.emailToken })
          });
          const data = await response.json();
          if (!response.ok || !data.ok || data.emailed !== true) throw new Error(data.error || 'send_failed');
          entry.hidden = true;
          success.hidden = false;
          dialog.setAttribute('aria-labelledby', success.querySelector('h2').id);
          this.querySelector('[data-email-recipient]').textContent = email;
          this.querySelector('[data-email-done]').focus();
        } catch (err) {
          console.error('Zconnect estimate send failed:', err.message);
          error.textContent = err.message === 'rate_limited' ? 'Too many requests. Please try again in an hour.'
            : err.message === 'estimate_changed' ? 'The estimate has changed. Refresh this page and try again.'
            : err.message === 'turnstile_failed' ? 'Please complete the verification and try again.'
            : err.message === 'email_failed' ? 'We couldn’t send your estimate. Please try again later.'
            : 'We couldn’t confirm the email was sent. Check your inbox before trying again.';
          this.emailToken = '';
          if (window.turnstile && this.widget !== undefined) window.turnstile.reset(this.widget);
        } finally {
          clearTimeout(timeout);
          this.sending = false;
          submit.textContent = 'Send estimate';
          submit.disabled = !this.emailToken;
          this.querySelector('[data-email-close]').disabled = false;
        }
      });
    }
    update() {
      const usersInput = this.querySelector('[name=users]');
      const sitesInput = this.querySelector('[name=sites]');
      const windowsInput = this.querySelector('[name=windows_count]');
      const appleInput = this.querySelector('[name=apple_count]');
      const windowsSelected = this.querySelector('[name=windows]').checked;
      const appleSelected = this.querySelector('[name=apple]').checked;
      windowsInput.disabled = !windowsSelected;
      appleInput.disabled = !appleSelected;
      const networkSelected = this.querySelector('[name=network]').checked;
      sitesInput.disabled = !networkSelected;
      const validCount = input => input.validity.valid && input.value !== '';
      const deviceTotal = (windowsSelected ? Number(windowsInput.value) : 0) + (appleSelected ? Number(appleInput.value) : 0);
      const tooManyDevices = deviceTotal > 250;
      const valid = validCount(usersInput) && (!networkSelected || validCount(sitesInput)) && (!windowsSelected || validCount(windowsInput)) && (!appleSelected || validCount(appleInput)) && !tooManyDevices;
      const users = Number(usersInput.value);
      const sites = Number(sitesInput.value);
      const rate = Math.max(1, Number(this.dataset.rate) || 150);
      const selected = name => this.querySelector(`[name=${name}]`).checked;
      const money = amount => new Intl.NumberFormat('en-US', {style:'currency',currency:'USD',maximumFractionDigits:0}).format(amount);
      this.querySelector('[data-price-note]').textContent = `USD · one-time services · before tax · estimated effort at ${money(rate)}/hour`;
      const rows = [];
      if (selected('m365')) rows.push(['Microsoft 365 setup', 8 + users * 0.2]);
      // Zconnect planning assumptions, calibrated against published service offerings.
      // Platform setup includes policies, up to five standard apps, pilot testing and handover.
      // Shared architecture/documentation remains a separate line, without duplicating those hours.
      if (windowsSelected) rows.push(['Device management (Windows)', 20 + Number(windowsInput.value) * 0.4]);
      if (appleSelected) rows.push(['Device management (Apple)', 24 + Number(appleInput.value) * 0.4]);
      if (selected('network')) rows.push(['Network configuration', 2 + sites * 6]);
      const count = rows.length;
      if (count) {
        rows.unshift(['Discovery, architecture & documentation', 6 + count * 2 + (selected('network') ? sites * 0.8 : 0)]);
      }
      const totalHours = rows.reduce((sum, row) => sum + row[1], 0);
      const base = totalHours * rate;
      const formatHours = hours => new Intl.NumberFormat('en-US', {maximumFractionDigits:1}).format(hours);
      const range = `${money(Math.ceil(base * 1.15 / 100) * 100)}–${money(Math.ceil(base * 1.3 / 100) * 100)}`;
      const ready = valid && count > 0;
      this.querySelector('[data-range]').textContent = !valid ? (tooManyDevices ? 'Request a tailored estimate' : 'Check your project size') : count ? range : 'Choose your services';
      this.querySelector('[data-summary]').textContent = !valid ? (tooManyDevices ? 'For more than 250 devices across Windows and Apple, request a tailored estimate.' : 'Enter valid user, location, and selected device counts.') : count ? `${users} users · ${selected('network') ? sites + ' office locations' : 'Cloud services only'}` : 'Select at least one service to see a planning estimate.';
      const deviceSummary = [windowsSelected ? `${windowsInput.value} Windows devices` : '', appleSelected ? `${appleInput.value} Macs` : ''].filter(Boolean).join(' · ');
      if (valid && count && deviceSummary) this.querySelector('[data-summary]').textContent += ` · ${deviceSummary}`;
      const breakdown = this.querySelector('[data-breakdown]');
      breakdown.replaceChildren();
      if (ready) {
        const table = document.createElement('table');
        table.className = 'zie-breakdown-table';
        table.setAttribute('aria-label', 'Estimated service hours and prices');
        const head = document.createElement('thead');
        const header = document.createElement('tr');
        ['Service', 'Estimated hours', 'Price'].forEach(label => {
          const cell = document.createElement('th');
          cell.scope = 'col';
          cell.textContent = label;
          header.append(cell);
        });
        head.append(header);
        const body = document.createElement('tbody');
        const addRow = (parent, label, hours, amount) => {
          const row = document.createElement('tr');
          const name = document.createElement('th');
          name.scope = 'row';
          name.textContent = label;
          const effort = document.createElement('td');
          effort.textContent = formatHours(hours);
          const price = document.createElement('td');
          price.textContent = money(amount);
          row.append(name, effort, price);
          parent.append(row);
        };
        rows.forEach(([label, hours]) => addRow(body, label, hours, hours * rate));
        const foot = document.createElement('tfoot');
        addRow(foot, 'Base total', totalHours, base);
        table.append(head, body, foot);
        breakdown.append(table);
      }
      this.querySelector('[data-send]').disabled = !ready;
      this.estimatePayload = ready ? {
        version: '2026-09-25', rate, users, sites,
        m365: selected('m365'), network: networkSelected, windows: windowsSelected, apple: appleSelected,
        windows_count: Number(windowsInput.value), apple_count: Number(appleInput.value)
      } : null;

    }
  }
  customElements.define('zc-it-estimator', ITServiceEstimator);
})();
